import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import React, {Suspense} from 'react';
import {DownloadPanelSkeletons} from '@Components/download-panel/components/download-panel-skeletons/download-panel-skeletons';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import type {FallbackProps} from 'react-error-boundary';
import {ErrorBoundary as ReactErrorBoundary} from 'react-error-boundary';
import {DownloadPanelContent} from '@Components/download-panel/components/download-panel-content/download-panel-content';
import styles from './download-panel.module.scss';
import {logClientError} from '@Libraries/log.library';
import {DownloadPanelErrorSection} from '@Components/download-panel/components/download-panel-error-section/download-panel-error-section';
import {loadPosterData} from '@PosterWhiteboard/poster/poster-init';
import {getPosterObjectFromPosterBackendObject} from '@PosterWhiteboard/poster/poster-backend';
import {useAppSelector} from '@/hooks';
import {DownloadStateTypes} from '@Components/download-panel/download-panel.reducer';

export interface DownloadPanelProps {
  id: string;
  className?: string;
  posterHashedId?: string;
  posterObject?: PosterLoadObject;
  isOpenedInModal?: boolean;
  isOpenedInEditor?: boolean;
  isOpenedInPrintShop?: boolean;
}

export function DownloadPanel({className = '', isOpenedInPrintShop = false, isOpenedInEditor = true, isOpenedInModal = false, ...props}: DownloadPanelProps): ReactElement {
  const [posterObject, setPosterObject] = useState(props.posterObject);
  const currentProcessingOrderId = useAppSelector((state) => {
    return state.downloadDialog.currentProcessingOrderId;
  });
  const currentOrderState = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmap[currentProcessingOrderId ?? '']?.state;
  });

  const getAndSetPosterObject = async (): Promise<void> => {
    if (!props.posterHashedId) {
      return;
    }
    const {posterBackendObject} = await loadPosterData(props.posterHashedId);
    const posterLoadObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
    setPosterObject(posterLoadObject);
  };

  useEffect((): void => {
    if (!props.posterHashedId && props.posterObject) {
      setPosterObject(props.posterObject);
    }
  }, [props.posterObject]);

  useEffect((): void => {
    if (!currentOrderState || (currentOrderState && currentOrderState === DownloadStateTypes.DOWNLOADING)) {
      return;
    }

    if (props.posterHashedId && props.posterHashedId !== posterObject?.hashedID) {
      void getAndSetPosterObject();
    }
  }, [currentOrderState]);

  useEffect(() => {
    if (currentOrderState && currentOrderState === DownloadStateTypes.DOWNLOADING) {
      return;
    }
    void getAndSetPosterObject();

    if (!posterObject && props.posterHashedId) {
      void getAndSetPosterObject();
    }
  }, [props.posterHashedId]);

  if (!posterObject && !props.posterHashedId) {
    throw new Error('expected poster object or poster hash id to be provided');
  }

  const getSkeletons = (): ReactElement => {
    return (
      <div className={`_full-width _overflow-auto _full-height spacing-p-t-3 spacing-p-b-3 spacing-p-r-4 spacing-p-l-4 ${styles.container}`}>
        <DownloadPanelSkeletons isPanelOpenedInModal={isOpenedInModal} />
      </div>
    );
  };

  const getContent = (): ReactElement => {
    if (!posterObject) {
      return getSkeletons();
    }
    return (
      <DownloadPanelContent
        isOpenedInPrintShop={isOpenedInPrintShop}
        isOpenedInEditor={isOpenedInEditor}
        isOpenedInModal={isOpenedInModal}
        posterObject={posterObject}
        id={props.id}
        className={className}
      />
    );
  };

  const onError = (e: Error): void => {
    void logClientError(e, {
      reason: 'DOWNLOAD_PANEL_ERROR_BOUNDARY',
    });
  };

  const getErrorFallback = ({error}: FallbackProps): ReactElement<unknown, typeof React.Component> => {
    return <DownloadPanelErrorSection errorMessage={error.message} />;
  };

  return (
    <ReactErrorBoundary fallbackRender={getErrorFallback} onError={onError}>
      <Suspense fallback={getSkeletons()}>{getContent()}</Suspense>
    </ReactErrorBoundary>
  );
}
